import { qotdModule } from '@/store/qotd/module'
import { quizModule } from '@/store/quiz/module'
import { bundleModule } from '@/store/bundle/module'
import { buildUTMParamString, isPromise } from '@/store/utils'
import type { Study } from '@pocketprep/types'
import type { ClientQuestion } from '@/store/qotd/state'

const getQotDSharingInfo = ({ bundleId, examGuid, version, userId, examName }: {
    bundleId: string
    examGuid: string
    version: string
    userId: string
    examName: string
}) => {
    const bundleBrandLib = bundleModule.getters.getBundleBrandLib()
    const bundleBrandInfo = bundleBrandLib[bundleId]
    const name = bundleBrandInfo?.universalLinkName || 'web'

    const queryParamString = `?examGuid=${examGuid}&version=${version}&userId=${userId}&bundleId=${bundleId}`

    return {
        title: `Can you answer today's ${(examName)} question?`,
        hashtags: 'itsyourturnnow,pocketprep',
        url: `https://pocketprep.com/share/${name}/qotd${queryParamString}&${buildUTMParamString({
            utm_campaign: 'app_share',
            utm_content: 'qotd',
        })}`,
    }
}

const getQotDQuestion = () => {
    const question = qotdModule.state.question.value

    if (isPromise(question)) {
        return null
    } else {
        return question
    }
}

/** ClientQuestion carries the flat nest `id`; the Parse `QuestionJSON` does not. */
const isClientQuestion = (
    question: Study.Class.QuestionJSON | ClientQuestion
): question is ClientQuestion => 'id' in question

/**
 * Build a genuine Parse `QuestionJSON` from a nest `ClientQuestion`. `UIKit.Question`
 * is typed to the Parse `toJSON` shape, so we faithfully re-wrap the fields that
 * differ from the flat nest contract: the pointer fields (`subject`, `examMetadata`,
 * `questionScenario`) and the `addedDate` Date wrapper. `createdAt`/`updatedAt` are
 * ISO strings in both shapes, so they pass through untouched.
 * @todo temporary until consumers read `API.Question` directly (WEB-2275)
 */
const clientQuestionToQuestionJSON = (question: ClientQuestion): Study.Class.QuestionJSON => {
    return {
        ...question,
        references: question.references ?? [],
        addedDate: { __type: 'Date', iso: question.addedDate },
        subject: { __type: 'Pointer', className: 'Subject', objectId: question.subject.objectId },
        examMetadata: { __type: 'Pointer', className: 'ExamMetadata', objectId: question.examMetadataId },
        // Pointer only: nest omits the expanded scenario, so ui-kit's "Part X of Y" badge silently hides (WEB-2275).
        questionScenario: question.questionScenarioId
            ? { __type: 'Pointer', className: 'QuestionScenario', objectId: question.questionScenarioId }
            : undefined,
    }
}

/**
 * Present the stored QotD question (which may be a nest `ClientQuestion`) as the
 * Parse `QuestionJSON` shape `UIKit.Question` requires.
 */
const getRenderableQotDQuestion = (
    question: Study.Class.QuestionJSON | ClientQuestion
): Study.Class.QuestionJSON =>
    isClientQuestion(question) ? clientQuestionToQuestionJSON(question) : question

const getQotDMetric = () => {
    const metric = qotdModule.state.globalMetric.value

    if (isPromise(metric)) {
        return null
    } else {
        return metric
    }
}

// Get today's submitted QotD quiz, if there is one
const getQotDQuiz = () => {
    const question = getQotDQuestion()

    if (!question) {
        return null
    }

    return quizModule.state.quizzes.find(quiz =>
        quiz.mode === -1
        && quiz.answers[0]?.questionSerial === question.serial
        && Math.abs(new Date(quiz.createdAt).getTime() - Date.now()) <= 86400000
    ) || null
}

export default {
    getQotDQuestion,
    getRenderableQotDQuestion,
    getQotDMetric,
    getQotDQuiz,
    getQotDSharingInfo,
}
